import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import Icon from '~/components/Icon';
import JustificationContainer from '~/components/JustificationContainer';
import useCollapseAnimation from '~/hooks/useCollapseAnimation';
import { animated } from 'react-spring';
import useScrollIntoView from '~/hooks/useScrollIntoView';
import { ExpandableLayout } from '~/graphql/types';

export type Props = Omit<ExpandableLayout, '__typename'> & {
  dataTestId?: string;
};

const Expandable: React.FC<Props> = ({
  children,
  title,
  margin,
  padding,
  ...rest
}) => {
  const [expanded, setExpanded] = useState(false);
  const [scrollToContainer, containerRef] = useScrollIntoView({});

  const { innerRef, outer, inner } = useCollapseAnimation({
    isOpen: expanded,
  });

  return (
    <JustificationContainer
      ref={containerRef}
      width="100%"
      direction="column"
      margin={margin ?? undefined}
      padding={padding ?? undefined}
      {...rest}
    >
      <ShowMoreContainer
        align="center"
        onClick={() => {
          setExpanded(prev => !prev);
          if (!expanded) scrollToContainer();
        }}
        width="100%"
        padding={['base']}
      >
        <Icon name={expanded ? 'chevron-up' : 'chevron'} margin={[null, 's']} />

        <>{expanded ? title?.expanded : title?.collapsed}</>
      </ShowMoreContainer>

      <animated.div
        style={{
          opacity: outer.opacity,
          height: outer.height,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <animated.div ref={innerRef} style={{ y: inner.y, width: '100%' }}>
          {children}
        </animated.div>
      </animated.div>
    </JustificationContainer>
  );
};

const ShowMoreContainer = styled(JustificationContainer)<{}>(
  ({ theme }) => css`
    color: ${theme.color('text', 'light')};
    cursor: pointer;

    &:hover {
      color: ${theme.color('primary', 'light')};
      transition: all 0.2s ease-in;
    }
  `,
);

export default Expandable;
